import 'react-native-url-polyfill/auto'
import { createClient, type Session } from '@supabase/supabase-js'
import type { ClientDatabase } from './database.client.types'
import { Platform } from 'react-native'
import { resetSupportForLogout } from './supportRuntime'
import { getOrCreateSignupAttribution } from './signupAttribution'
import { getE2ETestRunContext } from './e2eTestRun'
import { getClientSessionMetadata } from './clientSessionMetadata'
import { retryDiscoverySessionBootstrap } from './discoverySessionRetry'

const useLocal = process.env.EXPO_PUBLIC_USE_LOCAL_SUPABASE === 'true'

const supabaseUrl = useLocal
    ? process.env.EXPO_PUBLIC_SUPABASE_DEV_URL!
    : process.env.EXPO_PUBLIC_SUPABASE_URL!

const supabaseAnonKey = useLocal
    ? process.env.EXPO_PUBLIC_SUPABASE_DEV_ANON_KEY!
    : process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY!

// Custom logger function
const customLogger = (kind: string, msg: string, data: any) => {
    // We only want to log in development to avoid spamming production logs
    if (process.env.NODE_ENV !== 'development') {
        return;
    }

    // Use a switch to format different kinds of logs differently if you want
    switch (kind) {
        case 'phx_reply':
        case 'heartbeat':
            // These are very frequent, so you might want to hide them
            // by commenting out the console.log below.
            console.log(`[Supabase RT] ${msg}`, data);
            break;
        default:
            // Log everything else
            console.log(`[Supabase RT] [${kind}] ${msg}`, data || '');
    }
}

console.log(`🔐 [Supabase] Using ${useLocal ? 'LOCAL' : 'REMOTE'} instance:`, supabaseUrl);
export const supabase = createClient<ClientDatabase>(supabaseUrl, supabaseAnonKey, {
    auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: false,
    },
    realtime: {
        //logger: customLogger, // ONLY WHEN DEBUGGING 
    },
    global: {
        headers: {
            'X-Client-Info': 'supabase-js-web',
        },
    },
})

// Use local sign-out so browser logout does not revoke CLI or other device sessions.
export function signOutCurrentSession() {
    resetSupportForLogout()
    return supabase.auth.signOut({ scope: 'local' })
}

// Track connection state
let isSupabaseConnected = false;

export function connectSupabase(): boolean {
    // Only connect if we're on client and not already connected
    if (typeof window !== 'undefined' && !isSupabaseConnected) {
        console.log("🔗 [Supabase] Connecting after hydration...");

        // Connect realtime
        supabase.realtime.connect();

        // Mark as connected
        isSupabaseConnected = true;

        console.log("✅ [Supabase] Connected successfully!");
        return true;
    }

    return isSupabaseConnected;
}

export function isSupabaseReady(): boolean {
    return isSupabaseConnected;
}

const DISCOVERY_SESSION_PROPAGATION_ATTEMPTS = 3;
const DISCOVERY_SESSION_PROPAGATION_DELAY_MS = 200;

const wait = (ms: number): Promise<void> => new Promise((resolve) => {
    setTimeout(resolve, ms);
});

async function getCurrentSession(): Promise<Session | null> {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
        throw toError(error);
    }

    return session;
}

async function waitForSessionPropagation(): Promise<Session | null> {
    for (let attempt = 1; attempt <= DISCOVERY_SESSION_PROPAGATION_ATTEMPTS; attempt += 1) {
        const session = await getCurrentSession();

        if (session) {
            return session;
        }

        if (attempt < DISCOVERY_SESSION_PROPAGATION_ATTEMPTS) {
            await wait(DISCOVERY_SESSION_PROPAGATION_DELAY_MS);
        }
    }

    return null;
}

// TypeScript types for your database
// Utility function to check if an ID is a short ID (first part of UUID)
export const isShortId = (id: string): boolean => {
    // Full UUIDs have 5 segments separated by hyphens, short IDs have just the first segment
    return !id.includes('-') || id.split('-').length === 1;
};


// Helper to ensure we have a session (anonymous or real)
export async function ensureDiscoverySession() {
    let session = await getCurrentSession();

    if (!session) {
        // Create anonymous session
        console.log('🔐 [Discovery] Creating anonymous session...');
        const signupAttribution = getOrCreateSignupAttribution();
        const e2eTestContext = getE2ETestRunContext();
        const metadata: Record<string, unknown> = {
            ...getClientSessionMetadata(),
        };

        if (signupAttribution) {
            metadata.signup_attribution = signupAttribution;
        }

        if (e2eTestContext) {
            metadata.test_run = true;
            metadata.test_run_id = e2eTestContext.runId;
            metadata.test_run_tag = e2eTestContext.tag;
        }

        try {
            session = await retryDiscoverySessionBootstrap(async () => {
                const existingSession = await getCurrentSession();
                if (existingSession) {
                    return existingSession;
                }

                const { error } = await supabase.auth.signInAnonymously({
                    options: {
                        data: Object.keys(metadata).length > 0 ? metadata : undefined,
                    },
                });

                if (error) {
                    throw toError(error);
                }

                const establishedSession = await waitForSessionPropagation();
                if (!establishedSession) {
                    throw new Error('Session not established after signInAnonymously');
                }

                return establishedSession;
            }, {
                onRetry: ({ attempt, maxAttempts, delayMs, error }) => {
                    console.warn(
                        `🔁 [Discovery] Anonymous session bootstrap failed on attempt ${attempt}/${maxAttempts}. Retrying in ${delayMs}ms...`,
                        error
                    );
                },
            });
        } catch (error) {
            console.error('Failed to create anonymous session:', error);
            throw toError(error);
        }

        console.log('✅ [Discovery] Anonymous session created');
        return { session, isAnonymous: !session.user.email };
    }

    // Check if existing session is anonymous
    const isAnonymous = !session.user.email;
    return { session, isAnonymous };
}

// Convert Supabase error objects to proper Error instances for Sentry
// Supabase errors are plain objects with {code, message, details, hint}
// Throwing them directly causes Sentry to log "Object captured as exception with keys: ..."
export function toError(err: any): Error {
    if (err instanceof Error) return err;
    // i18n-ignore: technical-token -- normalized diagnostic is logged; views render descriptors.
    const message = err?.message || err?.details || err?.hint || 'Unknown Supabase error';
    const error = new Error(message);
    // Preserve original error properties for debugging
    (error as any).code = err?.code;
    (error as any).details = err?.details;
    (error as any).hint = err?.hint;
    return error;
}

// Use the official database types instead of manual definitions
export type { Database } from './database.types';
export type { Paper } from './database.client.types';     // paper table (discovery app)
export type { Papers } from './database.client.types';   // papers table (main app)
export type { Field, Subfield, Domain } from './database.client.types';
